import { DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { type ChangeEvent, useEffect, useState } from "react";

import CoAppTextField from "@/components/global/components/inputs/CoAppTextField";
import { CoAppActionButton, CoAppCancelTextButton, CoAppCloseIcon, CoAppDialog } from "@/components/global/styled/global.styled";

interface EditCollectionNameModalProps {
	dialogOpen: boolean;
	initialName: string;
	collectionId: string;
	onClose: () => void;
	onSave: (collectionId: string, name: string) => void | Promise<void>;
	isNameTaken: (name: string, excludeId?: string | number) => boolean;
}

export default function EditCollectionNameModal({
	dialogOpen,
	initialName,
	collectionId,
	onClose,
	onSave,
	isNameTaken,
}: EditCollectionNameModalProps) {
	const [name, setName] = useState(initialName);
	const [nameError, setNameError] = useState("");

	useEffect(() => {
		if (dialogOpen) {
			setName(initialName);
			setNameError("");
		}
	}, [dialogOpen, initialName]);

	const handleClose = () => {
		setName(initialName);
		setNameError("");
		onClose();
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		setName(e.target.value);
		setNameError("");
	};

	const handleSubmit = async () => {
		const trimmed = name.trim();
		if (!trimmed) {
			setNameError("Required *");
			return;
		}
		if (isNameTaken(trimmed, collectionId)) {
			setNameError("A collection with this name already exists.");
			return;
		}
		try {
			await onSave(collectionId, trimmed);
			handleClose();
		} catch {
			// toast from provider
		}
	};

	return (
		<CoAppDialog
			open={dialogOpen}
			onClose={handleClose}
			slotProps={{
				paper: {
					sx: {
						borderRadius: "8px",
						padding: "8px",
						width: "400px",
					},
				},
			}}
		>
			<div>
				<DialogTitle variant="h6">Edit collection name</DialogTitle>
				<CoAppCloseIcon onClick={handleClose} top={10} left={365} />
			</div>
			<DialogContentText
				variant="body2"
				color="text.primary"
				sx={{ marginLeft: "25px", width: "280px" }}
			>
				Rename this collection. Data type and platform cannot be changed.
			</DialogContentText>
			<DialogContent>
				<CoAppTextField
					value={name}
					onChange={handleChange}
					placeholder="Collection name"
					fullWidth
					error={nameError !== ""}
					helperText={nameError || "Required *"}
					FormHelperTextProps={{ sx: { color: nameError ? "#DE3730" : undefined } }}
				/>
			</DialogContent>
			<DialogActions sx={{ marginBottom: "15px", marginRight: "12px" }}>
				<CoAppCancelTextButton onClick={handleClose}>Cancel</CoAppCancelTextButton>
				<CoAppActionButton onClick={() => void handleSubmit()}>Save</CoAppActionButton>
			</DialogActions>
		</CoAppDialog>
	);
}
